import React, { useEffect, useState, useRef } from "react";
import { FormHelperText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import I18n from "../i18n.js";

import Checkbox from "../inputs/checkbox.js";
import { InputLabelDisplayModeAbove } from "../inputs/constants.js";
import DecimalField from "../inputs/decimal_field.js";

import { camelCase } from 'lodash';

import { 
  InvestmentExperienceName,
  InvestmentExperienceAllowValues,
  AlwaysVisibleQuestions
} from "../../questionnaire_constants.js";

const blockStyles = makeStyles(theme => ({
  root: {
  },
  disclaimer: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grayText,
    marginBottom: "40px",
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      lineHeight: "18px",
      marginBottom: "35px",
    },
  },
  questionContainer: {
    "& + &": {
      marginTop: "30px",
    }
  },
  question: {
    fontSize: "20px",
    lineHeight: "24px",
    marginBottom: "20px",
    ['@media (max-width: 768px)']: {
      fontSize: "14px",
      lineHeight: "18px",
      marginBottom: "0px",
    },
  },
  error: {
    color: theme.palette.redError
  },
  questionContent: {
  },
  questionVariants: {
    paddingLeft: "29px",
    listStyleImage: "none",
    ['@media (max-width: 768px)']: {
      paddingLeft: "20px",
    },
  },
  questionRow: {
    alignItems: "start",
  },
  questionVariant: {
    display: "block",
    "& + &": {
      marginTop: "-4px",
      ['@media (max-width: 768px)']: {
        marginTop: "-8px",
      },
    },
    "&:last-child": {
      marginBottom: "20px",
    }
  },
  questionVariantText: {
    fontSize: "16px",
    lineHeight: "18px",
    marginLeft: "11px",
    marginTop: "12px",
    ['@media (max-width: 768px)']: {
      fontSize: "13px",
      lineHeight: "16px",
      marginLeft: "5px",
      marginTop: "10px",
    },
  },
  questionValue: {
    marginLeft: "29px",
    "& .MuiFormControl-root": {
      width: "60%",
      ['@media (max-width: 768px)']: {
        width: "100%",
      },
    },
    ['@media (max-width: 768px)']: {
      marginTop: "20px",
      marginLeft: "0px",
    },
  }
}));

const QuestionnaireFields = (props) => {
  const classes = blockStyles();

  const { className, onChange } = props;
  const [questions, setQuestions] = useState(props.questions);
  const [errorData, setErrorData] = useState(props.errorData || {});
  const [disclaimer, setDisclaimer] = useState(props.disclaimer || I18n.t("component.questionnaire_fields.disclaimer"));
  const [showDisclaimer, setShowDisclaimer] = useState(props.disclaimer);

  useEffect(() => {
    setQuestions(props.questions);
  }, [props.questions]);

  useEffect(() => {
    setShowDisclaimer(props.showDisclaimer);
  }, [props.showDisclaimer]);

  useEffect(() => {
    if(props.disclaimer) {
      setDisclaimer(props.disclaimer);
    }
    
  }, [props.disclaimer]);

  useEffect(() => {
    setErrorData(props.errorData || {});
  }, [props.errorData]);

  const timeoutTokenRef = useRef();

  const onChangeSingle = (question, variant) => {
    const newQuestions = [...questions];
    const index = newQuestions.findIndex(item => item.key === question.key);

    newQuestions[index] = {
      ...question,
      singleValueKey: variant ? variant.key : undefined
    }
    setQuestions(newQuestions);
    
    if(onChange) {
      onChange(newQuestions);
    }
  }

  const onChangeMultiple = (question, variant) => {
    const newQuestions = [...questions];
    const index = newQuestions.findIndex(item => item.key === question.key);

    let currentValue = question.multipleValueKeys || [];
    if(currentValue.includes(variant.key)) {
      const index = currentValue.findIndex(v => v === variant.key);
      currentValue.splice(index, 1);
    }
    else {
      currentValue.push(variant.key);
    }
    currentValue.sort();

    newQuestions[index] = {
      ...question,
      multipleValueKeys: currentValue
    }
    setQuestions(newQuestions);

    if(onChange) {
      onChange(newQuestions);
    }
  }

  const onChangeNumber = (question, value) => {
    const newQuestions = [...questions];
    const index = newQuestions.findIndex(item => item.key === question.key);

    newQuestions[index] = {
      ...question,
      numberValue: value
    }
    setQuestions(newQuestions);

    if(timeoutTokenRef.current) {
      clearTimeout(timeoutTokenRef.current);
    }

    timeoutTokenRef.current = setTimeout(() => {
      clearTimeout(timeoutTokenRef.current);
      if(onChange) {
        onChange(newQuestions);
      }
    }, 500)
  }

  const questionContent = (question) => {
    if(question.type === "single") {
      return <ul className = { classes.questionVariants } >
        {
          question.availableValues.map((variant) => {
            return <li key = { variant.key } 
                       className = { classes.questionVariant } >
              <Checkbox checked = { question.singleValueKey === variant.key } 
                        onChange = { (value) => onChangeSingle(question, value ? variant : undefined) }
                        className = { classes.questionRow }
                        displayName = { 
                          <div className = { classes.questionVariantText } >
                            { variant.title }
                          </div>
                        } />
            </li>
          })
        }
      </ul>
    }
    if(question.type === "multiple") {
      return <ul className = { classes.questionVariants } >
        {
          question.availableValues.map((variant) => {
            return <li key = { variant.key } 
                       className = { classes.questionVariant } >
              <Checkbox checked = { (question.multipleValueKeys || []).includes(variant.key) } 
                        onChange = { () => onChangeMultiple(question, variant) }
                        className = { classes.questionRow }
                        displayName = { 
                          <div className = { classes.questionVariantText } >
                            { variant.title }
                          </div>
                        } />
            </li>
          })
        }
      </ul>
    }
    if(question.type === "number") {
      return <div className = { classes.questionValue } >
        <DecimalField value = { question.numberValue } 
                      onChange = { (value) => onChangeNumber(question, value) } 
                      type = { "number" }
                      displayName = { question.label }
                      displayNamePosition = { InputLabelDisplayModeAbove }
                      precision = { 12 }
                      scale = { 2 }  />
      </div>
    }
    return <React.Fragment />
  }

  const experienceQuestion = (questions.filter(q => q.key === InvestmentExperienceName)[0] || {});
  const experienceQuestionValue = experienceQuestion.singleValueKey;

  const displayQuestions = questions.filter((q) => {
    
    if(AlwaysVisibleQuestions.includes(q.key)) {
      return true;
    }
    return InvestmentExperienceAllowValues.includes(experienceQuestionValue)
  })

  return (
    <div className = { clsx(classes.root, className) } >
      {
        showDisclaimer && disclaimer &&
        <div className = { classes.disclaimer } >
          { disclaimer }
        </div>
      }
      {
        displayQuestions.map((question) => {
          const { fields: questionErrors = {} } = errorData || {};
          const errorMessage = questionErrors[camelCase(question.key)];
          return <div className = { classes.questionContainer } 
                      key = { question.key } >
            <div className = { clsx({
              [classes.question]: true,
              [classes.error]: Boolean(errorMessage)
            }) } >
              { question.title }
            </div>
            <div className = { classes.questionContent } >
              { questionContent(question) }
            </div>
            {
              errorMessage &&
              <FormHelperText error = { true } >
                { errorMessage }
              </FormHelperText>
            }
          </div>
        })
      }
    </div>
  );
}

export default QuestionnaireFields; 